import React, { useState } from 'react';
import { Upload, message } from 'antd';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';

interface ImageCropUploadProps {
  /** 裁剪宽高比, 如 1 表示 1:1, 16/9 表示 16:9 */
  aspect: number;
  /** 上传成功后回调，返回 URL */
  value?: string;
  onChange?: (url: string) => void;
  /** 提示文字 */
  hint?: string;
  /** 裁剪弹窗标题 */
  cropTitle?: string;
  /** 最大文件大小 MB */
  maxSizeMB?: number;
}

const ImageCropUpload: React.FC<ImageCropUploadProps> = ({
  aspect,
  value,
  onChange,
  hint,
  cropTitle,
  maxSizeMB = 5,
}) => {
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>(
    value
      ? [{ uid: '-1', name: 'current', status: 'done', url: value }]
      : []
  );

  const handleChange = (info: UploadChangeParam) => {
    const { file } = info;
    setFileList(info.fileList.slice(-1)); // 只保留最新一张

    if (file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (file.status === 'done') {
      setLoading(false);
      const url = file.response?.url;
      if (url) {
        onChange?.(url);
        setFileList([{ uid: file.uid, name: file.name, status: 'done', url }]);
      }
    }
    if (file.status === 'error') {
      setLoading(false);
      message.error(file.response?.error || '上传失败');
    }
  };

  const beforeUpload = (file: File) => {
    const isImage = /^image\/(jpeg|png|webp|gif|svg\+xml)$/.test(file.type);
    if (!isImage) {
      message.error('仅支持 JPG/PNG/WebP/GIF/SVG 格式');
      return false;
    }
    const isLt = file.size / 1024 / 1024 < maxSizeMB;
    if (!isLt) {
      message.error(`图片不能超过 ${maxSizeMB}MB`);
      return false;
    }
    return true;
  };

  const uploadButton = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, color: '#999' }}>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <span style={{ fontSize: 12 }}>{hint || '上传图片'}</span>
    </div>
  );

  const isSquare = Math.abs(aspect - 1) < 0.01;
  const previewWidth = isSquare ? 100 : 200;
  const previewHeight = isSquare ? 100 : Math.round(200 / aspect);

  return (
    <ImgCrop
      rotationSlider
      aspect={aspect}
      quality={0.9}
      modalTitle={cropTitle || '裁剪图片'}
      modalWidth={520}
    >
      <Upload
        name="file"
        action="/api/uploads/image"
        listType="picture-card"
        fileList={fileList}
        onChange={handleChange}
        beforeUpload={beforeUpload}
        maxCount={1}
        style={{ width: previewWidth }}
      >
        {fileList.length >= 1 ? null : uploadButton}
      </Upload>
    </ImgCrop>
  );
};

export default ImageCropUpload;
